// src/components/MyAppointments/MyAppointments.jsx
import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { Calendar, Clock, RefreshCw } from 'lucide-react';
import './MyAppointments.css';
import { getMyAppointments } from '../../Services/Service';

const MyAppointments = () => {
  const [activeTab, setActiveTab] = useState('future');
  const [futureAppointments, setFutureAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getMyAppointments();
      console.log('API Response:', response.data);

      let pastCycles = [];
      let futureCycles = [];
      
      if (response.data) {
        if (response.data.data?.past_cycles && Array.isArray(response.data.data.past_cycles)) {
          pastCycles = response.data.data.past_cycles;
        }
        if (response.data.data?.future_cycles && Array.isArray(response.data.data.future_cycles)) {
          futureCycles = response.data.data.future_cycles;
        }
      }

      console.log('Past Cycles:', pastCycles);
      console.log('Future Cycles:', futureCycles);

      const mapCycle = (cycle, index) => ({
        id: cycle.id || index,
        nhsId: cycle.user_id || 'N/A',
        consultationDate: cycle.next_consultation_date || cycle.cycle_end_date || cycle.cycle_start_date,
        status: cycle.cycle_status || cycle.report_status || 'Pending',
        cycleStartDate: cycle.cycle_start_date,
        cycleEndDate: cycle.cycle_end_date,
        lastConsultationDate: cycle.last_consultation_date,
        reportStatus: cycle.report_status,
        isOverdue: cycle.is_overdue === '1' || cycle.is_overdue === true,
        overdaysDays: cycle.overdue_days
      });
      
      const mappedPastAppointments = pastCycles.map(mapCycle);
      const mappedFutureAppointments = futureCycles.map(mapCycle);
      
      const sortedFuture = mappedFutureAppointments.sort((a, b) => 
        new Date(a.consultationDate) - new Date(b.consultationDate)
      );
      
      const sortedPast = mappedPastAppointments.sort((a, b) => 
        new Date(b.consultationDate) - new Date(a.consultationDate)
      );
      
      setFutureAppointments(sortedFuture);
      setPastAppointments(sortedPast);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments. Please try again.');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const statusConfig = {
      'scheduled': { className: 'status-scheduled', label: 'Scheduled' },
      'confirmed': { className: 'status-confirmed', label: 'Confirmed' },
      'pending': { className: 'status-pending', label: 'Pending' },
      'completed': { className: 'status-completed', label: 'Completed' },
      'cancelled': { className: 'status-cancelled', label: 'Cancelled' },
      'canceled': { className: 'status-cancelled', label: 'Cancelled' },
      'no show': { className: 'status-noshow', label: 'No Show' },
      'no_show': { className: 'status-noshow', label: 'No Show' },
      'noshow': { className: 'status-noshow', label: 'No Show' }
    };
    
    const config = statusConfig[statusLower] || { className: 'status-default', label: status || 'Unknown' };
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  const renderAppointmentCard = (appointment, isPast = false) => (
    <div 
      key={appointment.id || appointment.nhsId} 
      className={`appointment-card ${isPast ? 'past' : 'future'}`}
    >
      <div className="appointment-card-header">
        <div className="nhs-id-container">
          <span className="nhs-value">Status</span>
        </div>
        {getStatusBadge(appointment.status)}
      </div>
      
      <div className="appointment-card-body">
        <div className="date-time-row">
          <div className="date-info">
            <Calendar size={16} className="icon" />
            <span>{formatDate(appointment.consultationDate)}</span>
          </div>
          <div className="time-info">
            <Clock size={16} className="icon" />
            <span>{formatTime(appointment.consultationDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppointmentTable = (appointments, isPast = false) => (
    <div className="table-container">
      <table className="appointments-table">
        <thead>
          <tr>
            <th>NHS ID</th>
            <th>Consultation Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {appointments.map((apt, index) => (
            <tr key={apt.id || index} className={isPast ? 'past-row' : 'future-row'}>
              <td className="nhs-cell">{apt.nhsId}</td>
              <td className="date-cell">
                <div className="date-display">{formatDate(apt.consultationDate)}</div>
                <div className="time-display">{formatTime(apt.consultationDate)}</div>
              </td>
              <td className="status-cell">{getStatusBadge(apt.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderEmptyState = (type) => (
    <div className="empty-state">
      <Calendar size={48} className="empty-icon" />
      <h4>No {type} Appointments</h4>
      <p>You don't have any {type.toLowerCase()} appointments at the moment.</p>
    </div>
  );

  const currentAppointments = activeTab === 'future' ? futureAppointments : pastAppointments;

  return (
    <div className="my-appointments-container">
      {/* Header */}
      <div className="appointments-header">
        <h2>My Appointments</h2>
        <button className="refresh-btn" onClick={fetchAppointments}>
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'future' ? 'active' : ''}`}
          onClick={() => setActiveTab('future')}
        >
          <span className="tab-text">Up Coming</span>
          <span className="tab-count">{futureAppointments.length}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
          onClick={() => setActiveTab('past')}
        >
          <span className="tab-text">History</span>
          <span className="tab-count">{pastAppointments.length}</span>
        </button>
      </div>

      {/* Content */}
      <div className="appointments-content">
        {loading ? (
          <div className="loading-state">
            <Spinner animation="border" variant="primary" />
            <p>Loading appointments...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="bi bi-exclamation-triangle"></i>
            <p>{error}</p>
            <button onClick={fetchAppointments}>Retry</button>
          </div>
        ) : currentAppointments.length === 0 ? (
          renderEmptyState(activeTab === 'future' ? 'Future' : 'Past')
        ) : (
          <>
            {/* Card View for Mobile */}
            <div className="cards-view">
              {currentAppointments.map(apt => 
                renderAppointmentCard(apt, activeTab === 'past')
              )}
            </div>
            
            {/* Table View (for larger screens) */}
            <div className="table-view">
              {renderAppointmentTable(currentAppointments, activeTab === 'past')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyAppointments;